import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { cn } from '@/lib/cn'
import { modeLabelKey } from '@/lib/icons'
import type { CaseDetail } from '@/types/domain'

import { LegCard } from './LegCard'
import { RouteStrip } from './RouteStrip'

/* The case's directions, as a list — and NOTHING BUT a list (issue #35).
 *
 * Amending and deleting a direction moved to its own page in story 3, and
 * ADDING one has now followed it. That is not a relocation of controls: a
 * direction carries a route chain, a position on it, its trucks and — from
 * story 6 — its own money, none of which fits in a 440px sheet opened over a
 * list. The create was the last surface still saying otherwise, and it said it
 * in the most confusing possible way: the reader filled a modal, and the record
 * it made opened as a page.
 *
 * So this component now holds no mutation, no draft, no error surface and no
 * open/closed state. Every control on it is a way SOMEWHERE — the cards to the
 * directions they name (see `LegCard`), both «Добавить направление» buttons to
 * `legs/new`. The create's refusal, its half-landed template and the recovery
 * from it belong to the leg page's own DRAFT MODE now (`LegDraftDesk`), which
 * is the point: they were always about a record being made, and none of them
 * belonged to the list that happens to be behind it. */

export function LegsSection({ record }: { record: CaseDetail }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const legs = record.legs

  /* Both entries into the create, spelled ONCE. The empty state and the section
     header offer the same act, and two copies of this path would be two chances
     for one of them to rot. */
  const add = () => void navigate(`/cases/${record.id}/legs/new`)

  if (legs.length === 0) {
    return (
      <Card className="max-w-4xl">
        {/* An empty state says what the surface is FOR and offers the action
            that fills it — the add button belongs here, not only in a header. */}
        <EmptyState
          title={t('cases.legs.emptyTitle')}
          description={t('cases.legs.emptyDescription')}
          action={
            <Button size="sm" iconLeft={Plus} onClick={add}>
              {t('cases.legs.add')}
            </Button>
          }
        />
      </Card>
    )
  }

  /* "3 legs · rail + road via Khorgos" — the same breath the overview's route
     card takes: modes in leg order, deduplicated, and the border by name.
     Derived rather than served, restating facts already in this response. */
  const modes = [...new Set(legs.map((leg) => leg.mode))]
  const modeLine = modes
    .map((mode) => t(modeLabelKey(mode)).toLocaleLowerCase())
    .join(' + ')
  const border = record.route?.find((stop) => stop.role === 'border')

  /* Which leg the cargo is on, as the SERVER states it. `legnow` in casefile
     decides it — by reported state first and route order second — and this
     section only marks the answer. Re-deriving it here from the statuses below
     is the second definition that rule exists to prevent, and the two would
     part company the first time a leg was corrected backwards. */
  const current = record.currentLeg?.sequence

  return (
    /* Capped rather than filling the panel. A leg's facts are a STRIP — ETD,
       ETA, how long it takes, its paper — and a strip stretched across a wide
       monitor stops reading as one: the eye travels 400px from "31 авг." to the
       document that goes with it, and the four columns become four separate
       things. The cards keep their own measure and the panel keeps its
       whitespace. */
    <div className="flex max-w-4xl flex-col gap-3.5">
      {/* The section's one line of chrome, as on Participants: the count and
          the modes as quiet prose with the action at the far edge — no card
          header, because the route below IS the content and needs no title
          above it. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-text-muted text-[13px] font-light">
          {t('cases.legs.count', { count: legs.length })} ·{' '}
          {border
            ? t('cases.overview.routeVia', { modes: modeLine, border: border.name })
            : modeLine}
        </span>
        <Button variant="secondary" size="sm" iconLeft={Plus} onClick={add}>
          {t('cases.legs.add')}
        </Button>
      </div>

      <RouteStrip legs={legs} />

      {/* The legs as a numbered CHAIN rather than a stack. The rail carries the
          sequence and the joins, so the cards beside it are free to be cards:
          a reader following a shipment reads down the numbers, and the one the
          cargo is on is filled rather than outlined.

          Hidden below `sm`, where 28px of gutter is 28px a card does not get —
          the position is on every card anyway ("Leg 2"), and the rail is the
          part that only pays for itself once there is room to draw it. */}
      <ol className="flex flex-col gap-3">
        {legs.map((leg, index) => (
          <li key={leg.id} className="flex items-stretch gap-3">
            <div className="hidden w-7 shrink-0 flex-col items-center pt-3 sm:flex">
              <span
                className={cn(
                  'asa-num flex size-7 shrink-0 items-center justify-center rounded-full',
                  'border text-[12px] leading-none font-medium',
                  leg.sequence === current
                    ? 'border-brand bg-brand text-white'
                    : 'border-line bg-surface text-text-muted',
                )}
              >
                {/* The number restates the card's own "Leg 2" — decorative, so
                    it is not read twice. The marker beside it is not: where the
                    cargo is is a fact, and it must survive without colour. */}
                <span aria-hidden>{leg.sequence}</span>
                {leg.sequence === current ? (
                  <span className="sr-only">{t('cases.legs.currentLeg')}</span>
                ) : null}
              </span>
              {index < legs.length - 1 ? (
                <span aria-hidden className="bg-line-hair mt-1 w-px flex-1" />
              ) : null}
            </div>

            <div className="min-w-0 flex-1">
              <LegCard caseId={record.id} leg={leg} />
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}
